import { applicationToQuoteCards } from '@/components/quotes/applicationToQuoteCards'
import { PricedQuoteCardSkeleton } from '@/components/quotes/PricedQuoteCardSkeleton'
import { QuoteCardSkeleton } from '@/components/quotes/QuoteCardSkeleton'
import { PricingKind, type QuoteCardData, QuoteCardStage } from '@/components/quotes/quoteCardTypes'
import { GroupedCardDescription } from './GroupedCardDescription'
import { PackageCardDemo } from './PackageCardDemo'
import { INDICATION_FILTERS, QUOTED_FILTERS } from './pricedFilters'
import { PricedFilterView, type PricedItem } from './PricedFilterView'
import {
  APPLICATION_SENT_RESOLVED,
  DELLWOOD_DECLINED_AND_WAITING,
  DELLWOOD_LINES,
  DELLWOOD_PARTLY_DECLINED,
  describeQuote,
  QUOTES,
  UNIMPLEMENTED_QUOTES,
} from './quoteCardData'
import { QuoteCardResolveDemo } from './QuoteCardResolveDemo'
import { Cell, QuoteRow, Row } from './QuoteCardRow'
import { QuoteCardStateMatrix } from './QuoteCardStateMatrix'
import { QuoteCardTransitionDemo } from './QuoteCardTransitionDemo'
import { ReviewingCardDescription } from './ReviewingCardDescription'

export interface QuoteCardEntry {
  id: string
  /** The state being simulated — what the nav leads with. */
  state: string
  carrier?: string
  /** Shown above the mock. */
  description?: React.ReactNode
  content: React.ReactNode
}

export interface QuoteCardEntryGroup {
  /** Unset for rows that sit outside any group. */
  label?: string
  entries: QuoteCardEntry[]
}

type Live = (quote: QuoteCardData) => QuoteCardData

const APPLICATION_SENT_ID = 'canal-application-sent'

/** Notes on when production shows a fixture's state, keyed by fixture id. */
const DESCRIPTIONS: Record<string, React.ReactNode> = {
  'corgi-reviewing': <ReviewingCardDescription />,
}

const BEFORE_PRICING_STAGES = new Set([QuoteCardStage.PREPARING, QuoteCardStage.APPLICATION_SENT])
const BINDING_STAGES = new Set([QuoteCardStage.REQUEST_TO_BIND, QuoteCardStage.BOUND])
const NO_OFFER_STAGES = new Set([QuoteCardStage.DECLINED, QuoteCardStage.ERROR, QuoteCardStage.UNAVAILABLE])

const isDecline = (quote: QuoteCardData) =>
  NO_OFFER_STAGES.has(quote.stage) ||
  (quote.stage === QuoteCardStage.PRICED && quote.failedStage === QuoteCardStage.DECLINED)

/** describeQuote in sentence case, e.g. "Indication · reviewing submitted info"; PRICED is implied. */
function stateName(quote: QuoteCardData): string {
  const parts = describeQuote(quote).split(' · ')
  const shown = parts[0] === 'PRICED' && parts.length > 1 ? parts.slice(1) : parts
  const name = shown.map((part) => part.toLowerCase().replaceAll('_', ' ')).join(' · ')
  return name.charAt(0).toUpperCase() + name.slice(1)
}

function quoteEntry(quote: QuoteCardData, id = quote.id, description?: React.ReactNode): QuoteCardEntry {
  return {
    id,
    state: stateName(quote),
    carrier: quote.carrierName,
    description,
    content: <QuoteRow quote={quote} />,
  }
}

/** Every state on the page, grouped the way the side nav lists them. */
export function quoteCardGroups(live: Live): QuoteCardEntryGroup[] {
  const preparing = QUOTES.find((quote) => quote.stage === QuoteCardStage.PREPARING)

  const beforePricing: QuoteCardEntry[] = [
    {
      id: 'skeleton',
      state: 'Skeleton, carrier not yet known',
      content: (
        <Row
          label="skeleton, carrier not yet known"
          expanded={<QuoteCardSkeleton />}
          summary={<PricedQuoteCardSkeleton />}
        />
      ),
    },
  ]
  // Neither handoff animates in production yet.
  const transitions: QuoteCardEntry[] = []
  if (preparing) {
    transitions.push({
      id: 'skeleton-to-preparing',
      state: 'Skeleton → preparing',
      carrier: preparing.carrierName,
      content: (
        <Cell>
          <QuoteCardTransitionDemo quote={live(preparing)} label="SKELETON → PREPARING APPLICATION" />
        </Cell>
      ),
    })
  }
  const indications: (QuoteCardEntry & PricedItem)[] = []
  const quotes: (QuoteCardEntry & PricedItem)[] = []
  const binding: QuoteCardEntry[] = []
  const misc: QuoteCardEntry[] = [{ id: 'package-card', state: 'Package card', content: <PackageCardDemo /> }]
  const declines: QuoteCardEntry[] = []

  const place = (quote: QuoteCardData, id = quote.id, description = DESCRIPTIONS[quote.id]) => {
    const entry = quoteEntry(quote, id, description)
    if (isDecline(quote)) declines.push(entry)
    else if (BEFORE_PRICING_STAGES.has(quote.stage)) beforePricing.push(entry)
    else if (BINDING_STAGES.has(quote.stage)) binding.push(entry)
    else if (quote.stage === QuoteCardStage.PRICED && quote.appliedAt) binding.push(entry)
    else if (quote.stage === QuoteCardStage.PRICED && quote.isCustomerUploaded) misc.push(entry)
    else if (quote.stage === QuoteCardStage.PRICED) {
      ;(quote.pricingKind === PricingKind.INDICATION ? indications : quotes).push({ ...entry, quote })
    }
  }

  for (const fixture of QUOTES) {
    const quote = live(fixture)
    place(quote)

    if (quote.id === APPLICATION_SENT_ID) {
      transitions.push({
        id: 'application-sent-to-priced',
        state: 'Application sent → priced',
        carrier: quote.carrierName,
        content: (
          <Cell>
            <QuoteCardResolveDemo
              sent={quote}
              resolved={live(APPLICATION_SENT_RESOLVED)}
              label="APPLICATION SENT → PRICED"
            />
          </Cell>
        ),
      })
    }
  }

  // Keyed by scenario: the first two share their priced cargo line, so the mapped cards share an id.
  const dellwood = [
    { lines: DELLWOOD_LINES },
    { lines: DELLWOOD_PARTLY_DECLINED },
    { lines: DELLWOOD_DECLINED_AND_WAITING, description: <GroupedCardDescription /> },
  ]
  for (const [scenario, { lines, description }] of dellwood.entries()) {
    for (const quote of applicationToQuoteCards(lines)) {
      place(live(quote), `${scenario}-${quote.id}`, description)
    }
  }

  const prototypeOnly = [...transitions, ...UNIMPLEMENTED_QUOTES.map((quote) => quoteEntry(quote))]

  const pricedEntries: QuoteCardEntry[] = [
    {
      id: 'indications',
      state: 'Indication',
      content: <PricedFilterView items={indications} filters={INDICATION_FILTERS} />,
    },
    {
      id: 'quoted',
      state: 'Quoted',
      content: <PricedFilterView items={quotes} filters={QUOTED_FILTERS} />,
    },
  ]

  return [
    { label: 'Before pricing', entries: beforePricing },
    { label: 'Priced', entries: pricedEntries },
    { label: 'Binding', entries: binding },
    { label: 'Misc.', entries: misc },
    { label: 'Declines', entries: declines },
    { label: 'Prototype only', entries: prototypeOnly },
    { entries: [{ id: 'state-matrix', state: 'State matrix', content: <QuoteCardStateMatrix /> }] },
  ]
}
